import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { FloatingPanelState } from '../types';
import { NgButton } from '../buttons/NgButton';

export const FloatingPanelStateContext = createContext<FloatingPanelState>('docked');

export function useFloatingPanelState(): FloatingPanelState {
  return useContext(FloatingPanelStateContext);
}

export type FloatingPanelProps = {
  children?: ReactNode;
  state?: FloatingPanelState;
  onStateChanged?: (state: FloatingPanelState) => void | Promise<void>;
  showTopActionsAboveContent?: boolean;
  floatingDisabled?: boolean;
  floatingDisabledOnMobile?: boolean;
  disableFloating?: boolean;
  disableFullscreen?: boolean;
  cssClass?: string;
};

const PANEL_STATE_CSS: Record<FloatingPanelState, string> = {
  docked: 'floating-panel--state-docked',
  floating: 'floating-panel--state-floating',
  fullscreen: 'floating-panel--state-fullscreen',
};

const OVERLAY_CSS: Record<FloatingPanelState, string> = {
  docked: 'floating-panel__overlay--hide',
  floating: 'floating-panel__overlay--hide',
  fullscreen: 'floating-panel__overlay--show',
};

export function FloatingPanel({
  children,
  state: stateProp = 'docked',
  onStateChanged,
  showTopActionsAboveContent,
  floatingDisabled,
  floatingDisabledOnMobile,
  disableFloating,
  disableFullscreen,
  cssClass,
}: FloatingPanelProps) {
  const [state, setState] = useState<FloatingPanelState>(stateProp);

  useEffect(() => {
    setState(stateProp);
  }, [stateProp]);

  const effectiveState: FloatingPanelState = floatingDisabled ? 'docked' : state;

  const showTopActionButtonAboveContentCssClass = showTopActionsAboveContent
    ? 'floating-panel__top-action-panel--above-content'
    : '';
  const disabledOnMobileCssClass = floatingDisabledOnMobile
    ? 'floating-panel__top-action-panel--disabled-on-mobile'
    : '';

  const setFloatingState = (newState: FloatingPanelState) => {
    setState(newState);
    void onStateChanged?.(newState);
  };

  return (
    <>
      <div className={`floating-panel ${PANEL_STATE_CSS[effectiveState]} ${cssClass ?? ''}`}>
        {!floatingDisabled && (
          <div
            className={`floating-panel__top-action-panel ${showTopActionButtonAboveContentCssClass} ${disabledOnMobileCssClass}`}
          >
            {state !== 'docked' && (
              <NgButton
                icon="fal fa-square-arrow-up-right"
                outline
                cssClass="text-bg-light order-3"
                onClick={() => setFloatingState('docked')}
              />
            )}
            {state !== 'floating' && !disableFloating && (
              <NgButton
                icon={
                  state === 'docked'
                    ? 'fal fa-arrow-up-right-and-arrow-down-left-from-center'
                    : 'fal fa-arrow-down-left-and-arrow-up-right-to-center'
                }
                outline
                cssClass="text-bg-light order-2"
                onClick={() => setFloatingState('floating')}
              />
            )}
            {state !== 'fullscreen' && !disableFullscreen && (
              <NgButton
                icon="fal fa-arrows-maximize"
                type="dark"
                outline
                cssClass="text-bg-light order-1"
                onClick={() => setFloatingState('fullscreen')}
              />
            )}
          </div>
        )}

        <div className="floating-panel__content">
          <FloatingPanelStateContext.Provider value={state}>
            {children}
          </FloatingPanelStateContext.Provider>
        </div>
      </div>

      <div className={`floating-panel__overlay ${OVERLAY_CSS[effectiveState]}`}></div>
    </>
  );
}
